/**
 * applyMeasureState — AK-320 «Линейка на карте».
 *
 * Чистый reducer, переводящий ИЗМЕНЕНИЕ measureStore (prev → next) в
 * императивные вызовы MeasureController. Вынесен из подписки в v2.tsx,
 * чтобы покрыть юнит-тестом РЕ-ВХОДИМОСТЬ (re-entrant store-loop), которую
 * inline-замыкание скрывало от тестов.
 *
 * ── Почему это критично (P0, «Maximum call stack size exceeded»):
 * side-effect'ы контроллера (clear/deactivate/setMode/…) СИНХРОННО зовут
 * onResult → measureStore.setResult → set({result}) → это снова дёргает
 * подписку → повторный applyMeasureState(). Если prev-снапшот обновить
 * ПОСЛЕ side-effect'а, вложенный вызов увидит старый prevClearNonce → снова
 * clear() → бесконечная рекурсия.
 *
 * Решение: prev-снапшот ОБНОВЛЯЕМ ДО side-effect'ов, а сравниваем с локально
 * захваченными старыми значениями. Тогда вложенный apply увидит prev == next →
 * no-op, рекурсии нет.
 */
import type { MeasureMode, CircleMode } from './measureStore';

/** Поля measureStore, которые читает reducer (срез состояния). */
export interface MeasureSnapshot {
  active: boolean;
  mode: MeasureMode;
  circleMode: CircleMode;
  clearNonce: number;
  finalizeNonce: number;
}

/**
 * Мутируемый prev-снапшот. Передаётся по ссылке между вызовами reducer'а;
 * reducer обновляет его поля ДО вызова side-effect'ов (защита от ре-входа).
 */
export interface MeasurePrevRef {
  active: boolean;
  mode: MeasureMode;
  circleMode: CircleMode;
  clearNonce: number;
  finalizeNonce: number;
}

/** Императивная поверхность контроллера, которую дёргает reducer. */
export interface MeasureControllerLike {
  activate(mode: MeasureMode, circleMode?: CircleMode): void;
  deactivate(): void;
  setMode(mode: MeasureMode): void;
  setCircleMode(circleMode: CircleMode): void;
  clear(): void;
  finalizeFromUi(): void;
}

/** Создать prev-ref из начального состояния стора. */
export function initMeasurePrev(init: MeasureSnapshot): MeasurePrevRef {
  return {
    active: false,
    mode: init.mode,
    circleMode: init.circleMode,
    clearNonce: init.clearNonce,
    finalizeNonce: init.finalizeNonce,
  };
}

/**
 * Применить переход prev → next к контроллеру.
 *
 * ВАЖЕН ПОРЯДОК: сначала захватываем старый prev в локали, затем СРАЗУ
 * перезаписываем prev новым next, и ТОЛЬКО ПОТОМ дёргаем side-effect'ы.
 * Это делает reducer повторно-входимо-безопасным (re-entrant no-op).
 */
export function applyMeasureState(
  prev: MeasurePrevRef,
  next: MeasureSnapshot,
  ctrl: MeasureControllerLike,
): void {
  const pActive = prev.active;
  const pMode = prev.mode;
  const pCircleMode = prev.circleMode;
  const pClearNonce = prev.clearNonce;
  const pFinalizeNonce = prev.finalizeNonce;

  // Снимок ДО side-effect'ов: re-entrant apply увидит уже актуальные prev*.
  prev.active = next.active;
  prev.mode = next.mode;
  prev.circleMode = next.circleMode;
  prev.clearNonce = next.clearNonce;
  prev.finalizeNonce = next.finalizeNonce;

  if (next.active && !pActive) {
    ctrl.activate(next.mode, next.circleMode);
  } else if (!next.active && pActive) {
    ctrl.deactivate();
  } else if (next.active) {
    // mode и circleMode оба reset'ят рисунок — переключаем по факту смены.
    if (next.mode !== pMode) ctrl.setMode(next.mode);
    else if (next.circleMode !== pCircleMode) ctrl.setCircleMode(next.circleMode);
    // «Очистить» / «Готово» — монотонные nonce-сигналы (повтор того же mode
    // их НЕ триггерит, поэтому отдельные счётчики).
    if (next.clearNonce !== pClearNonce) ctrl.clear();
    if (next.finalizeNonce !== pFinalizeNonce) ctrl.finalizeFromUi();
  }
}
